import { c as _c } from "react/compiler-runtime";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckCircleIcon, DropIcon, LectureStatusIcon } from '@systemeio/ui/icons';
import { Tooltip } from '@systemeio/ui';
import { LectureId } from 'modules/courses/courses/curriculum/types/lecture-interface';
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props';
import React from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twMerge } from 'tailwind-merge';
import { getPageEditorUrl } from 'shared/utils/get-page-editor-url';
interface ModuleLectureProps {
  lecture: {
    id: LectureId;
    name: string;
    active: boolean;
    pageId?: number;
    delayBeforePreviousLecture: number;
    hasAssignmentSettings?: boolean;
    assignmentActivatedAt?: string | null;
  };
  disabled: boolean;
  isLastChild: boolean;
  isFetching?: boolean;
  lectureActions: React.ReactNode;
  onNameClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement | HTMLButtonElement>;
}
export function ModuleLecture(t0) {
  const $ = _c(74);
  const {
    lecture,
    disabled,
    isLastChild,
    isFetching,
    lectureActions,
    onNameClick
  } = t0;
  const {
    t,
    tICU
  } = useLocoTranslation();
  let t1;
  if ($[0] !== lecture.id) {
    t1 = {
      id: lecture.id
    };
    $[0] = lecture.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    setNodeRef,
    listeners,
    transform,
    transition
  } = useSortable(t1);
  let t2;
  if ($[2] !== transform) {
    t2 = CSS.Transform.toString(transform);
    $[2] = transform;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2 || $[5] !== transition) {
    t3 = {
      transform: t2,
      transition
    };
    $[4] = t2;
    $[5] = transition;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const style = t3;
  const t4 = isLastChild && "rounded-b-lg";
  const t5 = disabled && "cursor-not-allowed";
  const t6 = isFetching && "animate-pulse";
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = twMerge("flex flex-col ", t4, "even:bg-brand-primary-subtle odd:bg-background-secondary items-start gap-5 lg:flex-row lg:items-center", "hover:border-brand-primary main-transition-colors cursor-move border border-transparent", "p-4 lg:px-6 lg:py-4", t5, t6);
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = disabled ? undefined : setNodeRef;
  let t9;
  if ($[11] !== lecture.name || $[12] !== onNameClick) {
    t9 = <div className="text-brand-primary flex-[1_1_80%] cursor-default truncate text-sm font-normal" {...nonDraggableProps} onClick={onNameClick} data-test="module-lecture-name">{lecture.name}</div>;
    $[11] = lecture.name;
    $[12] = onNameClick;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== lecture.active || $[15] !== t) {
    t10 = lecture.active ? t("global.active") : t("global.inactive");
    $[14] = lecture.active;
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = `w-[20px]h-[20px] ${lecture.active && "fill-green"}`;
  let t12;
  if ($[17] !== t11) {
    t12 = <CheckCircleIcon className={t11} />;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t10 || $[20] !== t12) {
    t13 = <span className="text-text-primary whitespace-nowrap font-bold"><div className="flex cursor-default items-center lg:ml-4" {...nonDraggableProps}><Tooltip label={t10} key="remove-tooltip">{t12}</Tooltip></div></span>;
    $[19] = t10;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== lecture.assignmentActivatedAt || $[23] !== lecture.hasAssignmentSettings || $[24] !== t) {
    t14 = lecture.hasAssignmentSettings && <div {...nonDraggableProps}><Tooltip label={lecture.assignmentActivatedAt ? t("dashboard.course.lecture_assignment_active") : t("dashboard.course.lecture_assignment_inactive")}><LectureStatusIcon className={twMerge("h-[20px] w-[20px]", lecture.assignmentActivatedAt ? "fill-brand-primary" : "fill-fill-primary")} /></Tooltip></div>;
    $[22] = lecture.assignmentActivatedAt;
    $[23] = lecture.hasAssignmentSettings;
    $[24] = t;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t13 || $[27] !== t14) {
    t15 = <div className="flex flex-[1_1_20%] items-center  justify-between gap-3">{t13}{t14}</div>;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15 || $[30] !== t9) {
    t16 = <div className="block w-full truncate lg:hidden"><div className="flex items-center gap-3">{t9}{t15}</div></div>;
    $[29] = t15;
    $[30] = t9;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== lecture.name || $[33] !== lecture.pageId || $[34] !== onNameClick) {
    t17 = <div className="hidden w-full flex-[1_1_55%] truncate lg:block"><div className="flex items-center gap-3">{lecture.pageId ? <a className="primary-link cursor-pointer truncate text-sm font-normal" {...nonDraggableProps} href={getPageEditorUrl(lecture.pageId)} onClick={onNameClick} data-test="module-lecture-link">{lecture.name}</a> : <button className="primary-link cursor-pointer truncate text-sm font-normal" {...nonDraggableProps} onClick={onNameClick} data-test="module-lecture-name">{lecture.name}</button>}</div></div>;
    $[32] = lecture.name;
    $[33] = lecture.pageId;
    $[34] = onNameClick;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== lecture.active || $[37] !== t) {
    t18 = lecture.active ? t("global.active") : t("global.inactive");
    $[36] = lecture.active;
    $[37] = t;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = `h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${lecture.active && "fill-green"}`;
  let t20;
  if ($[39] !== t19) {
    t20 = <CheckCircleIcon className={t19} />;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t18 || $[42] !== t20) {
    t21 = <div className="hidden w-full flex-[2_0_10%] truncate lg:block "><div className="flex items-center gap-3"><span className="text-text-primary whitespace-nowrap font-bold"><div className="flex cursor-default items-center" {...nonDraggableProps} data-test="module-lecture-status"><Tooltip label={t18} key="remove-tooltip">{t20}</Tooltip></div></span></div></div>;
    $[41] = t18;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t) {
    t22 = t("dashboard.course.lecture_drip_note");
    $[44] = t;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const t23 = lecture.delayBeforePreviousLecture === 0;
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <DropIcon className="size-5" />;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== lecture.delayBeforePreviousLecture || $[48] !== t || $[49] !== tICU) {
    t25 = lecture.delayBeforePreviousLecture === 0 ? t("dashboard.course.lecture_drip_grant_access_immediately") : tICU("dashboard.course.lecture_drip_days_delay_title_icu", {
      daysCount: lecture.delayBeforePreviousLecture
    });
    $[47] = lecture.delayBeforePreviousLecture;
    $[48] = t;
    $[49] = tICU;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t25) {
    t26 = <span className="text-text-tertiary font-inter text-sm font-medium">{t25}</span>;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t22 || $[54] !== t23 || $[55] !== t26) {
    t27 = <div className="flex-[1_0_20%] lg:block"><div {...nonDraggableProps}><Tooltip label={t22} className="border-text-secondary h-7.5 flex w-fit items-center gap-1.5 rounded-full border px-3" disabled={t23}>{t24}{t26}</Tooltip></div></div>;
    $[53] = t22;
    $[54] = t23;
    $[55] = t26;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== lecture.assignmentActivatedAt || $[58] !== lecture.hasAssignmentSettings || $[59] !== t) {
    t28 = lecture.hasAssignmentSettings && <Tooltip wrapperClassName="lg:block hidden" label={lecture.assignmentActivatedAt ? t("dashboard.course.lecture_assignment_active") : t("dashboard.course.lecture_assignment_inactive")}><LectureStatusIcon className={twMerge("h-[20px] w-[20px]", lecture.assignmentActivatedAt ? "fill-brand-primary" : "fill-fill-primary")} /></Tooltip>;
    $[57] = lecture.assignmentActivatedAt;
    $[58] = lecture.hasAssignmentSettings;
    $[59] = t;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== lectureActions || $[62] !== t28) {
    t29 = <div className="flex items-center justify-end gap-6 lg:w-[100px] xl:flex-[1_0_10%]">{t28}{lectureActions}</div>;
    $[61] = lectureActions;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== listeners || $[65] !== style || $[66] !== t16 || $[67] !== t17 || $[68] !== t21 || $[69] !== t27 || $[70] !== t29 || $[71] !== t7 || $[72] !== t8) {
    t30 = <div className={t7} style={style} ref={t8} {...listeners}>{t16}{t17}{t21}{t27}{t29}</div>;
    $[64] = listeners;
    $[65] = style;
    $[66] = t16;
    $[67] = t17;
    $[68] = t21;
    $[69] = t27;
    $[70] = t29;
    $[71] = t7;
    $[72] = t8;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  return t30;
}